import { useState, useRef } from 'react';
import { Icon } from '../../utils/icons';
import api from '../../services/api';

const AppointmentBusinessProfile = ({ config, onConfigChange, onShowImageCropper: _onShowImageCropper }) => {
    const descriptionRef = useRef(null);
    const coverImageRef = useRef(null);
    const logoImageRef = useRef(null);
    const [uploadingCover, setUploadingCover] = useState(false);
    const [uploadingLogo, setUploadingLogo] = useState(false);

    const businessProfile = config.businessProfile || {};
    const showProfile = businessProfile.showProfile !== false; // Default to true

    const handleProfileToggle = (show) => {
        onConfigChange({
            businessProfile: {
                ...businessProfile,
                showProfile: show
            }
        });
    };

    const handleProfileChange = (field, value) => {
        onConfigChange({
            businessProfile: {
                ...businessProfile,
                [field]: value
            }
        });
    };

    const handleContactChange = (field, value) => {
        onConfigChange({
            businessProfile: {
                ...businessProfile,
                contact: {
                    ...businessProfile.contact,
                    [field]: value
                }
            }
        });
    };

    const handleUploadCover = () => {
        coverImageRef.current?.click();
    };

    const handleUploadLogo = () => {
        logoImageRef.current?.click();
    };

    const handleCoverImageChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            alert('Please select a valid image file (JPEG, PNG, or WebP)');
            return;
        }

        // Validate file size (5MB max)
        const maxSize = 5 * 1024 * 1024; // 5MB
        if (file.size > maxSize) {
            alert('File size must be less than 5MB');
            return;
        }

        setUploadingCover(true);

        try {
            // Create FormData for upload
            const formData = new FormData();
            formData.append('image', file);
            formData.append('imageType', 'cover');

            // Upload to S3
            const response = await api.post('/upload/widget-image', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (response.data.success) {
                handleProfileChange('coverImage', response.data.data.url);
            } else {
                throw new Error(response.data.message || 'Upload failed');
            }
        } catch (error) {
            console.error('Cover image upload error:', error);
            alert(error.message || 'Failed to upload cover image. Please try again.');
        } finally {
            setUploadingCover(false);
        }
    };

    const handleLogoImageChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            alert('Please select a valid image file (JPEG, PNG, or WebP)');
            return;
        }

        // Validate file size (5MB max)
        const maxSize = 5 * 1024 * 1024; // 5MB
        if (file.size > maxSize) {
            alert('File size must be less than 5MB');
            return;
        }

        setUploadingLogo(true);

        try {
            // Create FormData for upload
            const formData = new FormData();
            formData.append('image', file);
            formData.append('imageType', 'logo');

            // Upload to S3
            const response = await api.post('/upload/widget-image', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (response.data.success) {
                handleProfileChange('logo', response.data.data.url);
            } else {
                throw new Error(response.data.message || 'Upload failed');
            }
        } catch (error) {
            console.error('Logo upload error:', error);
            alert(error.message || 'Failed to upload logo. Please try again.');
        } finally {
            setUploadingLogo(false);
        }
    };

    // Rich text editor functions
    const applyFormatting = (command, value = null) => {
        document.execCommand(command, false, value);
        descriptionRef.current?.focus();
    };

    const handleDescriptionInput = (e) => {
        handleProfileChange('description', e.target.innerHTML);
    };

    const contact = businessProfile.contact || {};

    return (
        <div className="p-4 max-h-full overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Business Profile</h3>
            <p className="text-sm text-gray-400 mb-6">
                Display your business information and provide customers with additional contact options
            </p>

            {/* Show Business Profile Toggle */}
            <div className="mb-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h4 className="text-sm font-medium text-gray-300">Show Business Profile</h4>
                        <p className="text-xs text-gray-500 mt-1">
                            Display your business information and provide customers with additional contact options.
                            Also, business information can be used in email notifications.
                        </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                        <input
                            type="checkbox"
                            checked={showProfile}
                            onChange={(e) => handleProfileToggle(e.target.checked)}
                            className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                </div>
            </div>

            {showProfile && (
                <div className="space-y-6">
                    {/* Business Name */}
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                            Business Name
                        </label>
                        <input
                            type="text"
                            placeholder="Elite Cuts Salon"
                            value={businessProfile.businessName || ''}
                            onChange={(e) => handleProfileChange('businessName', e.target.value)}
                            className="w-full bg-gray-800 text-white px-3 py-2 rounded-lg border border-gray-700 focus:border-blue-500 focus:outline-none text-sm"
                        />
                    </div>

                    {/* Description with Rich Text Editor */}
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                            Description
                        </label>

                        {/* Rich Text Toolbar */}
                        <div className="bg-gray-800 border border-gray-700 rounded-t-lg p-3 overflow-x-auto">
                            <div className="flex items-center space-x-1 min-w-max">
                                {/* Text Formatting Group */}
                                <div className="flex items-center space-x-1">
                                    <button
                                        type="button"
                                        onClick={() => applyFormatting('bold')}
                                        className="px-2 py-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors font-bold text-sm"
                                        title="Bold (Ctrl+B)"
                                    >
                                        B
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => applyFormatting('italic')}
                                        className="px-2 py-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors italic text-sm"
                                        title="Italic (Ctrl+I)"
                                    >
                                        I
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => applyFormatting('underline')}
                                        className="px-2 py-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors underline text-sm"
                                        title="Underline (Ctrl+U)"
                                    >
                                        U
                                    </button>
                                </div>

                                <div className="w-px h-4 bg-gray-600"></div>

                                {/* Text Alignment Group */}
                                <div className="flex items-center space-x-1">
                                    <button
                                        type="button"
                                        onClick={() => applyFormatting('justifyLeft')}
                                        className="px-2 py-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors text-xs"
                                        title="Align Left"
                                    >
                                        ‹‹
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => applyFormatting('justifyCenter')}
                                        className="px-2 py-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors text-xs"
                                        title="Align Center"
                                    >
                                        ═
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => applyFormatting('justifyRight')}
                                        className="px-2 py-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors text-xs"
                                        title="Align Right"
                                    >
                                        ››
                                    </button>
                                </div>

                                <div className="w-px h-4 bg-gray-600"></div>

                                {/* Lists Group */}
                                <div className="flex items-center space-x-1">
                                    <button
                                        type="button"
                                        onClick={() => applyFormatting('insertUnorderedList')}
                                        className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors"
                                        title="Bullet List"
                                    >
                                        <Icon name="list" size={14} />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => applyFormatting('insertOrderedList')}
                                        className="px-2 py-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors text-xs"
                                        title="Numbered List"
                                    >
                                        1.
                                    </button>
                                </div>

                                <div className="w-px h-4 bg-gray-600"></div>

                                {/* Styling Group */}
                                <div className="flex items-center space-x-1">
                                    <select
                                        onChange={(e) => e.target.value && applyFormatting('foreColor', e.target.value)}
                                        className="bg-gray-700 text-white text-xs px-2 py-1 rounded border border-gray-600 focus:border-blue-500 focus:outline-none min-w-[60px]"
                                        title="Text Color"
                                        defaultValue=""
                                    >
                                        <option value="">Color</option>
                                        <option value="#ffffff">White</option>
                                        <option value="#e5e7eb">Light Gray</option>
                                        <option value="#9ca3af">Gray</option>
                                        <option value="#6b7280">Dark Gray</option>
                                        <option value="#1f2937">Black</option>
                                        <option value="#3b82f6">Blue</option>
                                        <option value="#10b981">Green</option>
                                        <option value="#f59e0b">Orange</option>
                                        <option value="#ef4444">Red</option>
                                        <option value="#8b5cf6">Purple</option>
                                    </select>

                                    <select
                                        onChange={(e) => e.target.value && applyFormatting('fontSize', e.target.value)}
                                        className="bg-gray-700 text-white text-xs px-2 py-1 rounded border border-gray-600 focus:border-blue-500 focus:outline-none min-w-[50px]"
                                        title="Font Size"
                                        defaultValue=""
                                    >
                                        <option value="">Size</option>
                                        <option value="1">S</option>
                                        <option value="3">M</option>
                                        <option value="4">L</option>
                                        <option value="5">XL</option>
                                        <option value="6">XXL</option>
                                    </select>
                                </div>

                                <div className="w-px h-4 bg-gray-600"></div>

                                {/* Utility Group */}
                                <div className="flex items-center space-x-1">
                                    <button
                                        type="button"
                                        onClick={() => applyFormatting('removeFormat')}
                                        className="px-2 py-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors text-xs"
                                        title="Clear Formatting"
                                    >
                                        Clear
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Editable Content */}
                        <div
                            ref={descriptionRef}
                            contentEditable
                            onInput={handleDescriptionInput}
                            onFocus={() => { }}
                            onBlur={() => { }}
                            className="w-full bg-gray-800 text-white px-4 py-3 rounded-b-lg border border-gray-700 border-t-0 focus:border-blue-500 focus:outline-none text-sm min-h-[100px] max-h-40 overflow-y-auto leading-relaxed"
                            style={{
                                whiteSpace: 'pre-wrap',
                                wordWrap: 'break-word'
                            }}
                            dangerouslySetInnerHTML={{
                                __html: businessProfile.description || '<div>Describe your business here... Use the toolbar above to format your text with bold, italic, colors, alignment, and more!</div>'
                            }}
                            placeholder="Describe your business here..."
                        />
                    </div>

                    {/* Image Uploads */}
                    <div className="space-y-4">
                        {/* Cover Upload */}
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                Cover Image
                            </label>
                            <div className="space-y-2">
                                {businessProfile.coverImage && (
                                    <div className="relative">
                                        <img
                                            src={businessProfile.coverImage}
                                            alt="Cover"
                                            className="w-full h-24 object-cover rounded-lg"
                                        />
                                        <button
                                            onClick={() => handleProfileChange('coverImage', '')}
                                            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-colors"
                                        >
                                            <Icon name="close" size={12} />
                                        </button>
                                    </div>
                                )}
                                <input
                                    type="file"
                                    ref={coverImageRef}
                                    onChange={handleCoverImageChange}
                                    accept="image/*"
                                    className="hidden"
                                />
                                <button
                                    onClick={handleUploadCover}
                                    disabled={uploadingCover}
                                    className="w-full bg-gray-800 hover:bg-gray-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-gray-300 py-2 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2 border border-gray-700"
                                >
                                    {uploadingCover ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-300"></div>
                                            <span className="text-sm">Uploading...</span>
                                        </>
                                    ) : (
                                        <>
                                            <Icon name="upload" size={16} />
                                            <span className="text-sm">Upload Cover Image</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Logo Upload */}
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                Logo
                            </label>
                            <div className="space-y-2">
                                {businessProfile.logo && (
                                    <div className="relative inline-block">
                                        <img
                                            src={businessProfile.logo}
                                            alt="Logo"
                                            className="w-16 h-16 object-cover rounded-lg"
                                        />
                                        <button
                                            onClick={() => handleProfileChange('logo', '')}
                                            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-colors"
                                        >
                                            <Icon name="close" size={12} />
                                        </button>
                                    </div>
                                )}
                                <input
                                    type="file"
                                    ref={logoImageRef}
                                    onChange={handleLogoImageChange}
                                    accept="image/*"
                                    className="hidden"
                                />
                                <button
                                    onClick={handleUploadLogo}
                                    disabled={uploadingLogo}
                                    className="w-full bg-gray-800 hover:bg-gray-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-gray-300 py-2 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2 border border-gray-700"
                                >
                                    {uploadingLogo ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-300"></div>
                                            <span className="text-sm">Uploading...</span>
                                        </>
                                    ) : (
                                        <>
                                            <Icon name="upload" size={16} />
                                            <span className="text-sm">Upload Logo</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Contact Information */}
                    <div>
                        <h4 className="text-sm font-medium text-gray-300 mb-4 border-b border-gray-700 pb-2">Contact Information</h4>

                        <div className="space-y-4">
                            {/* Phone */}
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Phone
                                </label>
                                <input
                                    type="tel"
                                    placeholder="(555) 123-4567"
                                    value={contact.phone || ''}
                                    onChange={(e) => handleContactChange('phone', e.target.value)}
                                    className="w-full bg-gray-800 text-white px-3 py-2 rounded-lg border border-gray-700 focus:border-blue-500 focus:outline-none text-sm"
                                />
                            </div>

                            {/* WhatsApp */}
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    WhatsApp
                                </label>
                                <input
                                    type="tel"
                                    placeholder="+1 (555) 123-4567"
                                    value={contact.whatsapp || ''}
                                    onChange={(e) => handleContactChange('whatsapp', e.target.value)}
                                    className="w-full bg-gray-800 text-white px-3 py-2 rounded-lg border border-gray-700 focus:border-blue-500 focus:outline-none text-sm"
                                />
                            </div>

                            {/* Instagram */}
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Instagram
                                </label>
                                <input
                                    type="text"
                                    placeholder="@elitecuts_salon"
                                    value={contact.instagram || ''}
                                    onChange={(e) => handleContactChange('instagram', e.target.value)}
                                    className="w-full bg-gray-800 text-white px-3 py-2 rounded-lg border border-gray-700 focus:border-blue-500 focus:outline-none text-sm"
                                />
                            </div>

                            {/* Address */}
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Address
                                </label>
                                <textarea
                                    rows={2}
                                    placeholder="1234 Fashion Avenue, New York, NY 10001"
                                    value={contact.address || ''}
                                    onChange={(e) => handleContactChange('address', e.target.value)}
                                    className="w-full bg-gray-800 text-white px-3 py-2 rounded-lg border border-gray-700 focus:border-blue-500 focus:outline-none text-sm resize-none"
                                />
                            </div>

                            {/* Email */}
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Email
                                </label>
                                <input
                                    type="email"
                                    placeholder="contact@elitecutssalon.com"
                                    value={contact.email || ''}
                                    onChange={(e) => handleContactChange('email', e.target.value)}
                                    className="w-full bg-gray-800 text-white px-3 py-2 rounded-lg border border-gray-700 focus:border-blue-500 focus:outline-none text-sm"
                                />
                            </div>

                            {/* Website */}
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Website
                                </label>
                                <input
                                    type="url"
                                    placeholder="https://www.elitecutssalon.com"
                                    value={contact.website || ''}
                                    onChange={(e) => handleContactChange('website', e.target.value)}
                                    className="w-full bg-gray-800 text-white px-3 py-2 rounded-lg border border-gray-700 focus:border-blue-500 focus:outline-none text-sm"
                                />
                            </div>
                        </div>
                    </div>

                </div>
            )}
        </div>
    );
};

export default AppointmentBusinessProfile;
